/**
 * Protocolo `postMessage` entre a UI (MockProvider) e o worker do `packages/sim` (SPEC 8.1).
 * Só mensagens serializáveis (structured clone), "como se fosse a rede".
 */
import type { LiveMessage, PainelProviderV2, ProviderError, ScenarioId, Topic } from '@rota/contracts';

/**
 * Métodos do provider do Painel chamados por RPC (tudo menos `kind`, `subscribe` e `sim`), com os
 * opcionais do `PainelProviderV2` (V2 6.2), que o core do worker implementa.
 */
export type CallMethod = Exclude<keyof PainelProviderV2, 'kind' | 'subscribe' | 'sim'>;
export type SimMethod = 'sim.get' | 'sim.set' | 'sim.resetDay';

/** Opções iniciais da simulação (URL `?cenario=&seed=&hora=&vel=`; exigem `sim:control`). */
export interface MockCoreOptions {
  scenario?: ScenarioId;
  /** Cenário da família V2 (`realista`…; SPEC-v2 7.6) dos métodos do Painel V2. */
  scenarioV2?: string;
  seed?: string;
  /** "HH:MM": relógio fixo (o dia é reconstruído até essa hora). */
  fixedAt?: string;
  speed?: 1 | 10 | 60;
  /** Dia simulado (AAAA-MM-DD); no modo ao vivo, hoje (as vendas reais entram por cima). */
  dateKey?: string;
}

export type ToWorker =
  | { type: 'init'; options: MockCoreOptions }
  /**
   * A UI pintou a primeira tela: o worker começa a 2ª fase do boot (motor de regras, decisões e
   * briefings; 10.4) e pré-carrega as consultas das outras telas.
   */
  | { type: 'warm' }
  | { type: 'call'; id: number; method: CallMethod | SimMethod; args: unknown[] }
  | { type: 'subscribe'; subId: number; topics: Topic[]; lastSeq?: number }
  | { type: 'unsubscribe'; subId: number };

export type FromWorker =
  | { type: 'ready'; buildMs: number }
  | { type: 'unavailable'; reason: string }
  | { type: 'result'; id: number; ok: true; value: unknown }
  | { type: 'result'; id: number; ok: false; error: ProviderError }
  | { type: 'live'; subId: number; message: LiveMessage };

export const SCENARIOS: readonly ScenarioId[] = [
  'normal',
  'recorde',
  'checkout_caiu',
  'cartao_caiu',
  'pagina_lenta',
  'webhook_silencioso',
  'criativo_fadigado',
  'upsell_quebrado',
  'madrugada_vazia',
];

export function isScenario(v: unknown): v is ScenarioId {
  return typeof v === 'string' && (SCENARIOS as readonly string[]).includes(v);
}

/** Cenários da família V2 (SPEC-v2 7.6). O padrão da V2 é o `realista`. */
export const SCENARIOS_V2 = [
  'realista',
  'realista_rastreado',
  'teste_131',
  'upsell_v2',
  'backend_pipeline',
  'organico_viral',
  'fatia_saturada',
  'automacao_pausada',
  'token_expirando',
  'fila_humana_cheia',
  'restricao_conta',
] as const;

export function isScenarioV2(v: unknown): v is (typeof SCENARIOS_V2)[number] {
  return typeof v === 'string' && (SCENARIOS_V2 as readonly string[]).includes(v);
}

/**
 * Canal entre os workers da simulação (V2-A0): o worker do Painel publica os comandos do Painel V2
 * (backend, plano, economia, links) e o worker da suíte (e o Painel das outras abas) os reaplica no
 * seu mundo V2. Mensagem: `PainelCommand` de `@rota/sim/painelV2` (com a chave `cenário|semente`).
 */
export const PAINEL_COMMAND_CHANNEL = 'rota-sim-painel-v2';

/** O que os hosts usam de um `BroadcastChannel` (o real, ou um par em memória nos testes). */
export interface CommandChannel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (e: MessageEvent<unknown>) => void): void;
  close?(): void;
}

/** O canal do navegador, quando existe (todos os navegadores atuais; nos testes, nenhum). */
export function commandChannel(): CommandChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(PAINEL_COMMAND_CHANNEL);
}
